<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-50">
    <div class="text-center">
      <div v-if="loading" class="mb-4">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-4 text-gray-600">Procesando autenticación...</p>
      </div>
      <div v-else-if="error" class="text-red-600">
        <h2 class="text-xl font-bold mb-2">Error de autenticación</h2>
        <p class="mb-4">{{ error }}</p>
        <button 
          @click="redirectToLogin" 
          class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Intentar de nuevo
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CognitoCallback',
  data() {
    return {
      loading: true,
      error: null
    }
  },
  mounted() {
    this.handleCallback()
  },
  methods: {
    handleCallback() {
      try {
        // Si no hay hash, verificar si ya está autenticado
        if (!window.location.hash) {
          const token = localStorage.getItem('token')
          const expiresAt = localStorage.getItem('expires_at')
          
          if (token && expiresAt && parseInt(expiresAt) > Date.now()) {
            // Ya está autenticado, redirigir al menú
            this.$router.push({ name: 'MobileLayoutView' })
            return
          } else {
            // No está autenticado, redirigir a login
            this.$router.push({ name: 'Login' })
            return
          }
        }
        
        // Extraer tokens del hash fragment que regresa Cognito
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        
        const accessToken = hashParams.get('access_token')
        const idToken = hashParams.get('id_token')
        const tokenType = hashParams.get('token_type')
        const expiresIn = hashParams.get('expires_in')
        
        if (accessToken && idToken) {
          // Decodificar el ID token para obtener información del usuario
          const userInfo = this.decodeJWT(idToken)
          
          // Decodificar el access token para obtener el rol
          const accessTokenInfo = this.decodeJWT(accessToken)
          
          // Calcular fecha de expiración
          const expirationTime = Date.now() + (parseInt(expiresIn) * 1000)
          
          // Extraer rol del access token (ahí es donde está en custom:permissions)
          let userRole = null
          
          if (accessTokenInfo['custom:permissions']) {
            try {
              const permissionsString = accessTokenInfo['custom:permissions']
              const permissions = JSON.parse(permissionsString)
              
              if (permissions.roles && permissions.roles.length > 0) {
                // Obtener el ID del primer rol
                userRole = permissions.roles[0].id
              }
            } catch (parseError) {
              // Error parseando custom:permissions - silencioso
            }
          }
          
          // Fallback a otros campos si no se encontró en custom:permissions
          if (!userRole) {
            userRole = accessTokenInfo.role || 
                      accessTokenInfo['custom:role'] || 
                      accessTokenInfo['custom:user_role'] ||
                      accessTokenInfo['cognito:groups']?.[0] || 
                      accessTokenInfo.groups?.[0] ||
                      userInfo['custom:role'] || 
                      userInfo.role ||
                      null
          }
          
          // Guardar tokens y información del usuario en localStorage
          localStorage.setItem('token', accessToken)
          localStorage.setItem('id_token', idToken)
          localStorage.setItem('token_type', tokenType)
          localStorage.setItem('expires_at', expirationTime.toString())
          localStorage.setItem('user', JSON.stringify({
            email: userInfo.email,
            name: userInfo.name || userInfo.given_name || userInfo.family_name || userInfo.email?.split('@')[0],
            given_name: userInfo.given_name,
            family_name: userInfo.family_name,
            sub: userInfo.sub,
            cognito_username: userInfo['cognito:username'],
            role: userRole
          }))
          
          // También guardar el rol por separado para compatibilidad
          if (userRole) {
            localStorage.setItem('role', userRole)
          }
          
          // Limpiar el hash de la URL
          window.history.replaceState({}, document.title, window.location.pathname)

          // Redirigir al menú principal
          this.$router.replace({ path: '/menu' }).then(() => {
            setTimeout(() => {
              // Emitir evento global de autenticación completada
              window.dispatchEvent(new CustomEvent('cognito-auth-complete', {
                detail: { userRole, userInfo }
              }))

              // Reload solo una vez para asegurar que el menú se cargue correctamente
              const already = sessionStorage.getItem('cognito:menuReloaded')
              if (!already) {
                sessionStorage.setItem('cognito:menuReloaded', '1')
                setTimeout(() => {
                  window.location.reload()
                }, 100)
              }
            }, 100)
          })
          
        } else {
          // Verificar si hay error en los parámetros
          const error = hashParams.get('error')
          const errorDescription = hashParams.get('error_description')
          
          if (error) {
            this.error = errorDescription || `Error: ${error}`
          } else {
            this.error = 'No se recibieron tokens válidos de Cognito'
          }
        }
      } catch (err) {
        console.error('Error procesando callback de Cognito:', err)
        this.error = 'Error interno procesando la autenticación'
      } finally {
        this.loading = false
      }
    },
    
    decodeJWT(token) {
      try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        }).join(''))
        
        return JSON.parse(jsonPayload)
      } catch (error) {
        console.error('Error decodificando JWT:', error)
        throw new Error('Token JWT inválido')
      }
    },
    
    redirectToLogin() {
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>